import { useEffect, useState } from 'react'
import { api, urls } from '../../../services/api'
import { IVideo } from '../../../types/video'

import { Button } from '../../ui/Button'
import { motion } from 'framer-motion'
import { Play, ChevronRight } from 'lucide-react'

import { SectionTitle } from '../../ui/SectionTitle'
import { Section } from '../../ui/Section'
import { Skeleton } from '../../ui/Skeleton'

interface Props {
	mobile: boolean
}

export function Videos({ mobile }: Props) {
	const [videos, setVideos] = useState<IVideo[]>([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		(async () => {
			try {
				const response = await api.get<IVideo[]>(urls.videosDestaque)
				setVideos(response.data.slice(0, 2))
			} catch (error) {
				 // console.error('Videos fetch error:', error);
			} finally {
				setLoading(false);
			}
		})()
	}, [])

	if (loading) {
		return (
			<Section>
				<div className="w-full flex items-center justify-center gap-6 lg:gap-8 mb-8">
					<div className="hidden lg:block flex-1 h-px bg-border-default/20" />
					<Skeleton className="h-8 w-48" />
					<div className="flex-1 h-px bg-border-default/20" />
				</div>
				<div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10">
					<Skeleton className="aspect-video rounded-2xl w-full" />
					<Skeleton className="aspect-video rounded-2xl w-full" />
				</div>
			</Section>
		);
	}

	if (videos.length < 1) return null;

	return (
		<Section>

			{/* Header Section */}
			<SectionTitle
				title="Vídeos em Destaque"
				icon={<Play className="fill-secondary text-secondary" size={24} />}
			/>

			{/* Videos Grid */}
			<div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10">
				{videos.map((video, index) => (
					<motion.div
						key={video.ytId || index}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.2 }}
						transition={{ delay: index * 0.2 }}
						className="flex flex-col gap-4 group"
					>
						<div className="relative aspect-video rounded-2xl overflow-hidden shadow-premium dark:shadow-premium-dark group-hover:shadow-premium-hover dark:group-hover:shadow-premium-dark-hover transition-all duration-300">
							<iframe
								src={`https://www.youtube.com/embed/${video.ytId}`}
								className="w-full h-full border-none"
								title={video.title}
								allowFullScreen
							></iframe>
						</div>

						<a
							href={`https://www.youtube.com/watch?v=${video.ytId}`}
							target='_blank'
							rel="noreferrer"
							className="text-lg lg:text-xl font-bold text-primary dark:text-on-surface-dark no-underline transition-all duration-300 px-2 group-hover:text-secondary-dark dark:group-hover:text-secondary group-hover:translate-x-2"
						>
							{video.title}
						</a>
					</motion.div>
				))}
			</div>

			{/* Footer CTA */}
			<motion.div
				initial={{ opacity: 0 }}
				whileInView={{ opacity: 1 }}
				viewport={{ once: true, amount: 0.2 }}
				className="mt-12 w-full flex justify-center"
			>
				<Button
					variant="primary"
					size="lg"
					className="w-full flex items-center justify-center gap-2 group mb-8"
					onClick={() => window.location.href = '/videos'}
				>
					<span>Mais Vídeos</span>
					<ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
				</Button>
			</motion.div>
		</Section>
	)
}